import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Dices, Plus, Search, Trophy, Users, Eye, Pencil, Trash2, Info } from 'lucide-react'
import { useSorteos, useEliminarSorteo } from '../hooks/useSorteos'
import type { FiltrosSorteos } from '../types/sorteos.types'

export function SorteosListPage() {
  const navigate = useNavigate()
  const [filtros, setFiltros] = useState<FiltrosSorteos>({ page: 1, page_size: 25 })
  const [search, setSearch] = useState('')
  const [showInfo, setShowInfo] = useState(false)
  const eliminar = useEliminarSorteo()
  const { data, isLoading } = useSorteos(filtros)

  // Búsqueda en tiempo real con debounce
  useEffect(() => {
    const timeout = setTimeout(() => {
      setFiltros(prev => ({ ...prev, search, page: 1 }))
    }, 300)
    return () => clearTimeout(timeout)
  }, [search])

  const totals = {
    total: data?.count ?? 0,
    activos: data?.results?.filter(s => s.activo).length ?? 0,
    participantes: data?.results?.reduce((sum, s) => sum + s.total_participantes, 0) ?? 0,
    ganadores: data?.results?.reduce((sum, s) => sum + s.total_ganadores, 0) ?? 0,
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Dices className="h-6 w-6 text-black dark:text-white" />
          <h1 className="text-2xl font-bold flex items-center gap-2">
            Sorteos
            <button
              onClick={() => setShowInfo(!showInfo)}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <Info className="h-5 w-5" />
            </button>
            {showInfo && (
              <span className="text-muted-foreground text-sm font-normal animate-in slide-in-from-left-2 duration-200">
                Gestión de sorteos y rifas
              </span>
            )}
          </h1>
        </div>
        <Button
          className="bg-teal-800 hover:bg-teal-900 text-white"
          onClick={() => navigate('/sorteos/crear')}
        >
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Sorteo
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Sorteos', value: totals.total, icon: Dices },
          { label: 'Activos', value: totals.activos, icon: Dices },
          { label: 'Participantes', value: totals.participantes, icon: Users },
          { label: 'Ganadores', value: totals.ganadores, icon: Trophy },
        ].map(({ label, value, icon: Icon }) => (
          <Card key={label} className="border border-gray-300 dark:border-gray-700 bg-gradient-to-r from-teal-500/15 via-teal-500/5 via-50% to-transparent dark:from-teal-800/20 dark:via-teal-900/10 dark:via-50% dark:to-transparent">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <Icon className="h-5 w-5 text-teal-500" />
                <div>
                  <p className="text-sm text-muted-foreground">{label}</p>
                  <p className="text-2xl font-bold text-black dark:text-white">{value}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search */}
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Buscar sorteos..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* List */}
      {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500" />
            </div>
          ) : data?.results?.length === 0 ? (
            <div className="text-center py-12">
              <Dices className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-lg font-medium">No hay sorteos</p>
              <p className="text-muted-foreground">Crea tu primer sorteo para comenzar</p>
            </div>
          ) : (
          <Card className="border border-gray-300 dark:border-gray-700">
            <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-100 dark:bg-[hsl(220,15%,16%)]">
                  <TableHead>Nombre</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead className="text-center">Participantes</TableHead>
                  <TableHead className="text-center">Ganadores</TableHead>
                  <TableHead className="text-center">Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.results?.map((sorteo) => (
                  <TableRow key={sorteo.id}>
                    <TableCell className="font-medium">{sorteo.nombre}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {sorteo.tipo_ruleta === 'slot' ? 'Slot' : 'Circular'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">{sorteo.total_participantes}</TableCell>
                    <TableCell className="text-center">{sorteo.total_ganadores}/{sorteo.max_ganadores}</TableCell>
                    <TableCell className="text-center">
                      <Badge className={sorteo.activo ? 'badge-gradient' : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}>
                        {sorteo.activo ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          onClick={() => window.open(`/sorteos/${sorteo.id}/vista`, '_blank')}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          onClick={() => navigate(`/sorteos/${sorteo.id}/editar`)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-red-500 hover:text-red-700"
                          onClick={() => {
                            if (confirm(`¿Eliminar el sorteo "${sorteo.nombre}"?`)) {
                              eliminar.mutate(sorteo.id)
                            }
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </CardContent>
          </Card>
          )}

      {/* Pagination */}
      {data && data.total_pages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={filtros.page === 1}
            onClick={() => setFiltros(prev => ({ ...prev, page: (prev.page ?? 1) - 1 }))}
          >
            Anterior
          </Button>
          <span className="flex items-center px-3 text-sm text-muted-foreground">
            Página {filtros.page} de {data.total_pages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={filtros.page === data.total_pages}
            onClick={() => setFiltros(prev => ({ ...prev, page: (prev.page ?? 1) + 1 }))}
          >
            Siguiente
          </Button>
        </div>
      )}
    </div>
  )
}
